import { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, Save, ChevronDown, ChevronUp } from "lucide-react";
import { showError, showSuccess } from "../../lib/toast";
import { useNavigate } from "react-router-dom";
import {
  server_post_data,
  estimator_config_save,
  estimator_config_get,
} from "../../serviceconnection/ServiceConnection";

// ── Outside component ─────────────────────────────────────────────────────────
const InputField = ({
  label,
  placeholder,
  type = "text",
  value,
  onChange,
  textarea,
}) => (
  <div className="space-y-1">
    <label className="text-xs font-semibold text-muted-foreground">
      {label}
    </label>
    {textarea ? (
      <textarea
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        rows={3}
        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent resize-none"
      />
    ) : (
      <input
        type={type}
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
      />
    )}
  </div>
);

const CollapsibleSection = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="section-panel p-0">
      <button
        onClick={() => setOpen((p) => !p)}
        className="w-full section-panel-header flex items-center justify-between cursor-pointer hover:bg-muted/30 transition-colors"
      >
        <h3 className="section-panel-title">{title}</h3>
        {open ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        )}
      </button>
      {open && <div className="section-panel-body space-y-4">{children}</div>}
    </div>
  );
};

// ── Default seeds ─────────────────────────────────────────────────────────────
const EMPTY_CONFIG = {
  hero_badge: "Planning Tool",
  hero_title: "SAP Business One",
  hero_title_highlight: "Implementation Cost Estimator",
  hero_description:
    "Understand the investment involved in an SAP Business One implementation before you start the conversation.",
  base_cost: { low: 25000, high: 50000 },
  business_size_options: [
    { label: "5–20 users", low: 0, high: 0, weeks_low: 8, weeks_high: 14 },
    {
      label: "20–50 users",
      low: 20000,
      high: 40000,
      weeks_low: 12,
      weeks_high: 20,
    },
    {
      label: "50–100 users",
      low: 50000,
      high: 90000,
      weeks_low: 18,
      weeks_high: 28,
    },
    {
      label: "100+ users",
      low: 100000,
      high: 180000,
      weeks_low: 24,
      weeks_high: 40,
    },
  ],
  current_system_options: [
    { label: "Manual / Excel", low: 5000, high: 15000 },
    { label: "MYOB", low: 8000, high: 18000 },
    { label: "Xero", low: 8000, high: 18000 },
    { label: "QuickBooks", low: 8000, high: 18000 },
    { label: "Another ERP", low: 15000, high: 35000 },
    { label: "SAP B1 (upgrade)", low: 5000, high: 12000 },
  ],
  modules_options: [
    {
      id: "finance",
      label: "Finance",
      low: 0,
      high: 0,
      weeks_low: 0,
      weeks_high: 0,
    },
    {
      id: "inventory",
      label: "Inventory",
      low: 5000,
      high: 12000,
      weeks_low: 1,
      weeks_high: 2,
    },
    {
      id: "sales",
      label: "Sales & CRM",
      low: 5000,
      high: 12000,
      weeks_low: 1,
      weeks_high: 2,
    },
    {
      id: "purchasing",
      label: "Purchasing",
      low: 3000,
      high: 8000,
      weeks_low: 1,
      weeks_high: 1,
    },
    {
      id: "manufacturing",
      label: "Manufacturing",
      low: 15000,
      high: 35000,
      weeks_low: 3,
      weeks_high: 6,
    },
    {
      id: "wms",
      label: "WMS",
      low: 12000,
      high: 28000,
      weeks_low: 2,
      weeks_high: 5,
    },
    {
      id: "ecommerce",
      label: "eCommerce",
      low: 10000,
      high: 25000,
      weeks_low: 2,
      weeks_high: 4,
    },
  ],
  data_migration_options: [
    { label: "Minimal (COA + open balances)", low: 2000, high: 6000, weeks: 1 },
    {
      label: "Moderate (+ customer/supplier/item master)",
      low: 8000,
      high: 18000,
      weeks: 2,
    },
    { label: "Extensive (+ full history)", low: 20000, high: 45000, weeks: 4 },
  ],
  integration_options: [
    { label: "None", low: 0, high: 0, weeks: 0 },
    { label: "Simple (1–2 systems)", low: 8000, high: 20000, weeks: 2 },
    { label: "Complex (3+ systems)", low: 25000, high: 60000, weeks: 6 },
  ],
  implementation_phases: [
    {
      name: "Discovery & Planning",
      duration: "2–4 weeks",
      description: "Requirements gathering, process mapping, project planning",
    },
    {
      name: "System Configuration",
      duration: "4–8 weeks",
      description: "Core setup, master data migration, workflow configuration",
    },
    {
      name: "Development & Integration",
      duration: "4–12 weeks",
      description: "Customisations, integrations, reporting setup",
    },
    {
      name: "Testing & Training",
      duration: "3 – 6 weeks",
      description: "UAT, end-user training, documentation",
    },
    {
      name: "Go-Live & Support",
      duration: "2–4 weeks",
      description: "Deployment, hyper-care support, optimisation",
    },
  ],
  estimate_includes: [
    "Discovery & requirements analysis",
    "System configuration & setup",
    "Data migration assistance",
    "Training & change management",
    "Go-live support & hyper-care",
  ],
  cta_heading: "Ready to Start Your ERP Journey?",
  cta_description:
    "Let our experienced consultants create a detailed implementation roadmap tailored to your business.",
  cta_btn_text: "Book a Discovery Session",
  cta_btn_link: "/contact",
  disclaimer_text:
    "These estimates are indicative only. Every implementation is unique. Contact us for a detailed scoping exercise and accurate proposal.",
};

export default function EstimatorConfig() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);

  const sc = useCallback(
    (key, val) => setConfig((p) => ({ ...p, [key]: val })),
    [],
  );

  // ── Fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const fd = new FormData();
        const res = await server_post_data(estimator_config_get, fd);
        const d = res.data?.data;
        if (d && Object.keys(d).length > 0)
          setConfig({ ...EMPTY_CONFIG, ...d });
      } catch {
        showError("Failed to load config");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    try {
      setSaveLoading(true);
      const fd = new FormData();
      const scalars = [
        "hero_badge",
        "hero_title",
        "hero_title_highlight",
        "hero_description",
        "cta_heading",
        "cta_description",
        "cta_btn_text",
        "cta_btn_link",
        "disclaimer_text",
      ];
      scalars.forEach((k) => fd.append(k, config[k] ?? ""));
      fd.append("base_cost", JSON.stringify(config.base_cost));
      fd.append(
        "business_size_options",
        JSON.stringify(config.business_size_options),
      );
      fd.append(
        "current_system_options",
        JSON.stringify(config.current_system_options),
      );
      fd.append("modules_options", JSON.stringify(config.modules_options));
      fd.append(
        "data_migration_options",
        JSON.stringify(config.data_migration_options),
      );
      fd.append(
        "integration_options",
        JSON.stringify(config.integration_options),
      );
      fd.append(
        "implementation_phases",
        JSON.stringify(config.implementation_phases),
      );
      fd.append("estimate_includes", JSON.stringify(config.estimate_includes));

      const res = await server_post_data(estimator_config_save, fd);
      if (res.data?.error) {
        showError(res.data?.message);
        return;
      }
      showSuccess("Config saved!");
      navigate("/pages");
    } catch {
      showError("Something went wrong");
    } finally {
      setSaveLoading(false);
    }
  };

  // ── Array helpers ─────────────────────────────────────────────────────────
  const arrSet = (key, idx, field, val) =>
    setConfig((p) => {
      const arr = [...p[key]];
      arr[idx] = {
        ...arr[idx],
        [field]: ["label", "id", "name", "description", "duration"].includes(
          field,
        )
          ? val
          : Number(val) || 0,
      };
      return { ...p, [key]: arr };
    });
  const arrDelete = (key, idx) =>
    setConfig((p) => ({ ...p, [key]: p[key].filter((_, i) => i !== idx) }));
  const arrAdd = (key, template) =>
    setConfig((p) => ({ ...p, [key]: [...p[key], template] }));

  if (loading)
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">
        Loading...
      </div>
    );

  return (
    <div className="max-w-[1000px] mx-auto space-y-5">
      {/* ── Header ── */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Implementation Estimator Config</h1>
          <p className="page-subtitle">
            Manage calculator options, phases, and hero content shown on the
            website
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saveLoading}
          className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-60"
          style={{ background: "hsl(var(--accent))" }}
        >
          <Save className="w-4 h-4" />
          {saveLoading ? "Saving..." : "Save Changes"}
        </button>
      </div>

      {/* ── Hero Section ── */}
      <CollapsibleSection title="Hero Section" defaultOpen>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InputField
            label="Badge Text"
            placeholder="Planning Tool"
            value={config.hero_badge}
            onChange={(e) => sc("hero_badge", e.target.value)}
          />
          <InputField
            label="Title"
            placeholder="SAP Business One"
            value={config.hero_title}
            onChange={(e) => sc("hero_title", e.target.value)}
          />
        </div>
        <InputField
          label="Title Highlight"
          placeholder="Implementation Cost Estimator"
          value={config.hero_title_highlight}
          onChange={(e) => sc("hero_title_highlight", e.target.value)}
        />
        <InputField
          label="Description"
          textarea
          placeholder="Subtitle text..."
          value={config.hero_description}
          onChange={(e) => sc("hero_description", e.target.value)}
        />
      </CollapsibleSection>

      {/* ── Base Cost ── */}
      <CollapsibleSection
        title="Base Cost (AUD — professional services)"
        defaultOpen
      >
        <div className="grid grid-cols-2 gap-4 max-w-sm">
          <InputField
            label="Low (AUD)"
            type="number"
            value={config.base_cost?.low ?? ""}
            onChange={(e) =>
              setConfig((p) => ({
                ...p,
                base_cost: { ...p.base_cost, low: Number(e.target.value) || 0 },
              }))
            }
          />
          <InputField
            label="High (AUD)"
            type="number"
            value={config.base_cost?.high ?? ""}
            onChange={(e) =>
              setConfig((p) => ({
                ...p,
                base_cost: {
                  ...p.base_cost,
                  high: Number(e.target.value) || 0,
                },
              }))
            }
          />
        </div>
      </CollapsibleSection>

      {/* ── Business Size ── */}
      <CollapsibleSection title="Business Size Options">
        <div className="space-y-3">
          {config.business_size_options.map((item, idx) => (
            <div
              key={idx}
              className="grid grid-cols-[1fr_90px_90px_80px_80px_36px] gap-3 items-end p-3 rounded-lg border border-border bg-muted/10"
            >
              <InputField
                label={idx === 0 ? "Label" : ""}
                placeholder="e.g. 5–20 users"
                value={item.label}
                onChange={(e) =>
                  arrSet("business_size_options", idx, "label", e.target.value)
                }
              />
              <InputField
                label={idx === 0 ? "Low (AUD)" : ""}
                type="number"
                value={item.low}
                onChange={(e) =>
                  arrSet("business_size_options", idx, "low", e.target.value)
                }
              />
              <InputField
                label={idx === 0 ? "High (AUD)" : ""}
                type="number"
                value={item.high}
                onChange={(e) =>
                  arrSet("business_size_options", idx, "high", e.target.value)
                }
              />
              <InputField
                label={idx === 0 ? "Wks Low" : ""}
                type="number"
                value={item.weeks_low}
                onChange={(e) =>
                  arrSet(
                    "business_size_options",
                    idx,
                    "weeks_low",
                    e.target.value,
                  )
                }
              />
              <InputField
                label={idx === 0 ? "Wks High" : ""}
                type="number"
                value={item.weeks_high}
                onChange={(e) =>
                  arrSet(
                    "business_size_options",
                    idx,
                    "weeks_high",
                    e.target.value,
                  )
                }
              />
              <button
                onClick={() => arrDelete("business_size_options", idx)}
                className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-muted mb-0.5"
                style={{ color: "hsl(var(--destructive))" }}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button
            onClick={() =>
              arrAdd("business_size_options", {
                label: "",
                low: 0,
                high: 0,
                weeks_low: 8,
                weeks_high: 14,
              })
            }
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-border text-sm text-muted-foreground hover:text-foreground hover:border-accent transition-colors"
          >
            <Plus className="w-4 h-4" /> Add Option
          </button>
        </div>
      </CollapsibleSection>

      {/* ── Current System ── */}
      <CollapsibleSection title="Current System Options">
        <div className="space-y-3">
          {config.current_system_options.map((item, idx) => (
            <div
              key={idx}
              className="grid grid-cols-[1fr_100px_100px_36px] gap-3 items-end p-3 rounded-lg border border-border bg-muted/10"
            >
              <InputField
                label={idx === 0 ? "Label" : ""}
                placeholder="e.g. MYOB"
                value={item.label}
                onChange={(e) =>
                  arrSet("current_system_options", idx, "label", e.target.value)
                }
              />
              <InputField
                label={idx === 0 ? "Low (AUD)" : ""}
                type="number"
                value={item.low}
                onChange={(e) =>
                  arrSet("current_system_options", idx, "low", e.target.value)
                }
              />
              <InputField
                label={idx === 0 ? "High (AUD)" : ""}
                type="number"
                value={item.high}
                onChange={(e) =>
                  arrSet("current_system_options", idx, "high", e.target.value)
                }
              />
              <button
                onClick={() => arrDelete("current_system_options", idx)}
                className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-muted mb-0.5"
                style={{ color: "hsl(var(--destructive))" }}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button
            onClick={() =>
              arrAdd("current_system_options", { label: "", low: 0, high: 0 })
            }
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-border text-sm text-muted-foreground hover:text-foreground hover:border-accent transition-colors"
          >
            <Plus className="w-4 h-4" /> Add Option
          </button>
        </div>
      </CollapsibleSection>

      {/* ── Modules ── */}
      <CollapsibleSection title="Modules Options">
        <div className="space-y-3">
          {config.modules_options.map((item, idx) => (
            <div
              key={idx}
              className="grid grid-cols-[90px_1fr_85px_85px_75px_75px_36px] gap-3 items-end p-3 rounded-lg border border-border bg-muted/10"
            >
              <InputField
                label={idx === 0 ? "ID (unique)" : ""}
                placeholder="e.g. wms"
                value={item.id}
                onChange={(e) =>
                  arrSet("modules_options", idx, "id", e.target.value)
                }
              />
              <InputField
                label={idx === 0 ? "Label" : ""}
                placeholder="e.g. WMS"
                value={item.label}
                onChange={(e) =>
                  arrSet("modules_options", idx, "label", e.target.value)
                }
              />
              <InputField
                label={idx === 0 ? "Low (AUD)" : ""}
                type="number"
                value={item.low}
                onChange={(e) =>
                  arrSet("modules_options", idx, "low", e.target.value)
                }
              />
              <InputField
                label={idx === 0 ? "High (AUD)" : ""}
                type="number"
                value={item.high}
                onChange={(e) =>
                  arrSet("modules_options", idx, "high", e.target.value)
                }
              />
              <InputField
                label={idx === 0 ? "Wks Low" : ""}
                type="number"
                value={item.weeks_low}
                onChange={(e) =>
                  arrSet("modules_options", idx, "weeks_low", e.target.value)
                }
              />
              <InputField
                label={idx === 0 ? "Wks High" : ""}
                type="number"
                value={item.weeks_high}
                onChange={(e) =>
                  arrSet("modules_options", idx, "weeks_high", e.target.value)
                }
              />
              <button
                onClick={() => arrDelete("modules_options", idx)}
                className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-muted mb-0.5"
                style={{ color: "hsl(var(--destructive))" }}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button
            onClick={() =>
              arrAdd("modules_options", {
                id: `module_${Date.now()}`,
                label: "",
                low: 0,
                high: 0,
                weeks_low: 0,
                weeks_high: 0,
              })
            }
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-border text-sm text-muted-foreground hover:text-foreground hover:border-accent transition-colors"
          >
            <Plus className="w-4 h-4" /> Add Module
          </button>
        </div>
      </CollapsibleSection>

      {/* ── Data Migration ── */}
      <CollapsibleSection title="Data Migration Options">
        <div className="space-y-3">
          {config.data_migration_options.map((item, idx) => (
            <div
              key={idx}
              className="grid grid-cols-[1fr_100px_100px_80px_36px] gap-3 items-end p-3 rounded-lg border border-border bg-muted/10"
            >
              <InputField
                label={idx === 0 ? "Label" : ""}
                placeholder="e.g. Minimal"
                value={item.label}
                onChange={(e) =>
                  arrSet("data_migration_options", idx, "label", e.target.value)
                }
              />
              <InputField
                label={idx === 0 ? "Low (AUD)" : ""}
                type="number"
                value={item.low}
                onChange={(e) =>
                  arrSet("data_migration_options", idx, "low", e.target.value)
                }
              />
              <InputField
                label={idx === 0 ? "High (AUD)" : ""}
                type="number"
                value={item.high}
                onChange={(e) =>
                  arrSet("data_migration_options", idx, "high", e.target.value)
                }
              />
              <InputField
                label={idx === 0 ? "Weeks" : ""}
                type="number"
                value={item.weeks}
                onChange={(e) =>
                  arrSet("data_migration_options", idx, "weeks", e.target.value)
                }
              />
              <button
                onClick={() => arrDelete("data_migration_options", idx)}
                className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-muted mb-0.5"
                style={{ color: "hsl(var(--destructive))" }}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button
            onClick={() =>
              arrAdd("data_migration_options", {
                label: "",
                low: 0,
                high: 0,
                weeks: 0,
              })
            }
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-border text-sm text-muted-foreground hover:text-foreground hover:border-accent transition-colors"
          >
            <Plus className="w-4 h-4" /> Add Option
          </button>
        </div>
      </CollapsibleSection>

      {/* ── Integration ── */}
      <CollapsibleSection title="Integration Options">
        <div className="space-y-3">
          {config.integration_options.map((item, idx) => (
            <div
              key={idx}
              className="grid grid-cols-[1fr_100px_100px_80px_36px] gap-3 items-end p-3 rounded-lg border border-border bg-muted/10"
            >
              <InputField
                label={idx === 0 ? "Label" : ""}
                placeholder="e.g. None"
                value={item.label}
                onChange={(e) =>
                  arrSet("integration_options", idx, "label", e.target.value)
                }
              />
              <InputField
                label={idx === 0 ? "Low (AUD)" : ""}
                type="number"
                value={item.low}
                onChange={(e) =>
                  arrSet("integration_options", idx, "low", e.target.value)
                }
              />
              <InputField
                label={idx === 0 ? "High (AUD)" : ""}
                type="number"
                value={item.high}
                onChange={(e) =>
                  arrSet("integration_options", idx, "high", e.target.value)
                }
              />
              <InputField
                label={idx === 0 ? "Weeks" : ""}
                type="number"
                value={item.weeks}
                onChange={(e) =>
                  arrSet("integration_options", idx, "weeks", e.target.value)
                }
              />
              <button
                onClick={() => arrDelete("integration_options", idx)}
                className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-muted mb-0.5"
                style={{ color: "hsl(var(--destructive))" }}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button
            onClick={() =>
              arrAdd("integration_options", {
                label: "",
                low: 0,
                high: 0,
                weeks: 0,
              })
            }
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-border text-sm text-muted-foreground hover:text-foreground hover:border-accent transition-colors"
          >
            <Plus className="w-4 h-4" /> Add Option
          </button>
        </div>
      </CollapsibleSection>

      {/* ── Implementation Phases ── */}
      <CollapsibleSection title="Implementation Phases">
        <div className="space-y-3">
          {config.implementation_phases.map((item, idx) => (
            <div
              key={idx}
              className="p-3 rounded-lg border border-border bg-muted/10 space-y-2"
            >
              <div className="grid grid-cols-[1fr_140px_36px] gap-3 items-end">
                <InputField
                  label={idx === 0 ? "Phase Name" : ""}
                  placeholder="e.g. Discovery & Planning"
                  value={item.name}
                  onChange={(e) =>
                    arrSet("implementation_phases", idx, "name", e.target.value)
                  }
                />
                <InputField
                  label={idx === 0 ? "Duration" : ""}
                  placeholder="e.g. 2–4 weeks"
                  value={item.duration}
                  onChange={(e) =>
                    arrSet(
                      "implementation_phases",
                      idx,
                      "duration",
                      e.target.value,
                    )
                  }
                />
                <button
                  onClick={() => arrDelete("implementation_phases", idx)}
                  className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-muted mb-0.5"
                  style={{ color: "hsl(var(--destructive))" }}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <InputField
                label={idx === 0 ? "Description" : ""}
                placeholder="Brief description of this phase"
                value={item.description}
                onChange={(e) =>
                  arrSet(
                    "implementation_phases",
                    idx,
                    "description",
                    e.target.value,
                  )
                }
              />
            </div>
          ))}
          <button
            onClick={() =>
              arrAdd("implementation_phases", {
                name: "",
                duration: "",
                description: "",
              })
            }
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-border text-sm text-muted-foreground hover:text-foreground hover:border-accent transition-colors"
          >
            <Plus className="w-4 h-4" /> Add Phase
          </button>
        </div>
      </CollapsibleSection>

      {/* ── Estimate Includes ── */}
      <CollapsibleSection title="Estimate Includes List">
        <div className="space-y-2">
          {config.estimate_includes.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <input
                value={item}
                onChange={(e) =>
                  setConfig((p) => {
                    const arr = [...p.estimate_includes];
                    arr[idx] = e.target.value;
                    return { ...p, estimate_includes: arr };
                  })
                }
                placeholder={`Item ${idx + 1}`}
                className="flex-1 px-3 py-1.5 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
              />
              <button
                onClick={() =>
                  setConfig((p) => ({
                    ...p,
                    estimate_includes: p.estimate_includes.filter(
                      (_, i) => i !== idx,
                    ),
                  }))
                }
                className="w-7 h-7 rounded flex items-center justify-center hover:bg-muted flex-shrink-0"
                style={{ color: "hsl(var(--destructive))" }}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <button
            onClick={() =>
              setConfig((p) => ({
                ...p,
                estimate_includes: [...p.estimate_includes, ""],
              }))
            }
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Add Item
          </button>
        </div>
      </CollapsibleSection>

      {/* ── Disclaimer & CTA ── */}
      <CollapsibleSection title="Disclaimer & CTA">
        <InputField
          label="Disclaimer Text"
          textarea
          value={config.disclaimer_text}
          onChange={(e) => sc("disclaimer_text", e.target.value)}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
          <InputField
            label="CTA Heading"
            value={config.cta_heading}
            onChange={(e) => sc("cta_heading", e.target.value)}
          />
          <InputField
            label="CTA Button Text"
            value={config.cta_btn_text}
            onChange={(e) => sc("cta_btn_text", e.target.value)}
          />
        </div>
        <InputField
          label="CTA Description"
          textarea
          value={config.cta_description}
          onChange={(e) => sc("cta_description", e.target.value)}
        />
        <InputField
          label="CTA Button Link"
          placeholder="/contact"
          value={config.cta_btn_link}
          onChange={(e) => sc("cta_btn_link", e.target.value)}
        />
      </CollapsibleSection>

      {/* ── Bottom Save ── */}
      <div className="flex justify-end pb-6">
        <button
          onClick={handleSave}
          disabled={saveLoading}
          className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
          style={{ background: "hsl(var(--accent))" }}
        >
          <Save className="w-4 h-4" />
          {saveLoading ? "Saving..." : "Save All Changes"}
        </button>
      </div>
    </div>
  );
}
